/**
 * Preset FAB icon paths. Designed for a 24×24 viewBox and shared by the vanilla IIFE
 * (`src/ui/fab.ts`) and the React FloatingWidget so both stay visually identical.
 *
 * Defaults to `help` (a chat bubble with a `?`): it preserves the "open a conversation with
 * support" affordance users expect from widgets like Intercom/Zendesk, while adding a single
 * unambiguous cue that the surface is help/support rather than generic messaging. See the
 * design rationale in CHANGELOG 1.2.20.
 */

export type FabIconPreset = 'chat' | 'help'

/** Path data for each preset. Values are the `d` attribute of an SVG path. */
export const FAB_ICON_PATHS: Record<FabIconPreset, string> = {
  // Chat bubble — kept as an opt-in for integrations that want the classic Intercom-style glyph.
  chat: 'M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z',
  // Chat bubble + "?" — the new default. Material "contact_support" shape.
  help: 'M11.5 2C6.81 2 3 5.81 3 10.5S6.81 19 11.5 19h.5v3c4.86-2.34 8-7 8-11.5C20 5.81 16.19 2 11.5 2zm1 14.5h-2v-2h2v2zm0-3.5h-2c0-3.25 3-3 3-5 0-1.1-.9-2-2-2s-2 .9-2 2h-2c0-2.21 1.79-4 4-4s4 1.79 4 4c0 2.5-3 2.75-3 5z',
}

/** Close glyph shown when the widget is open. Not part of the public preset surface. */
export const FAB_CLOSE_PATH =
  'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'

export const DEFAULT_FAB_ICON: FabIconPreset = 'help'

/**
 * Resolves a user-provided `fabIcon` value to an SVG path string. Accepts a preset key,
 * a raw SVG path (`M…` string), or `undefined`/unknown (falls back to the default).
 * Paths are surfaced via `<path d="…">` inside a 24×24 viewBox, so custom shapes should
 * target that coordinate space.
 */
export function resolveFabIconPath(fabIcon: string | undefined): string {
  if (!fabIcon) return FAB_ICON_PATHS[DEFAULT_FAB_ICON]
  if (fabIcon in FAB_ICON_PATHS) return FAB_ICON_PATHS[fabIcon as FabIconPreset]
  // Raw SVG path starts with a move command. If it doesn't, fall back to the default so a
  // typoed config key doesn't blank out the FAB entirely.
  if (/^[Mm]\s*[-\d.]/.test(fabIcon.trim())) return fabIcon
  return FAB_ICON_PATHS[DEFAULT_FAB_ICON]
}
